import {concatXpath} from "./utils";

function quoteattr(s: string, preserveCR?: boolean): string {
    const preserveCRval = preserveCR ? '&#13;' : '\n';
    return ('' + s) /* Forces the conversion to string. */
        .replace(/&/g, '&amp;') /* This MUST be the 1st replacement. */
        .replace(/'/g, '&apos;') /* The 4 other predefined entities, required. */
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        /*
        You may add other replacements here for HTML only
        (but it's not necessary).
        Or for XML, only if the named entities are defined in its DTD.
        */
        .replace(/\r\n/g, preserveCRval) /* Must be before the next replacement. */
        .replace(/[\r\n]/g, preserveCRval);

}

/// Если в классах/айдишниках или других атрибутах HTML есть цифры, то скорее всего этот класс не подходит, т.к.
/// это что то типа "col-5" или "react-class-1223897541" и тп. Такие классы игнорируем.
function hasDigit(s: string): boolean {
    return s.search(/\d+/) >= 0
}

function getByClass(elem: HTMLElement, resp: Array<string>, strict: boolean = true) {
    // @ts-ignore
    for (const className of elem.classList.values()) {
        if (hasDigit(className) || className.startsWith("pi-")) {
            continue;
        }

        if (strict) {
            resp.push(
                `${elem.tagName.toLowerCase()}[contains(concat(" ", normalize-space(@class), " "), concat(" ", "${quoteattr(className)}", " "))]`
            )
        } else {
            resp.push(
                `${elem.tagName.toLowerCase()}[contains(@class, "${quoteattr(className)}")]`
            )
        }
    }
}

function getByID(elem: HTMLElement, resp: Array<string>, strict: boolean = true) {
    if (elem.id && !hasDigit(elem.id)) {
        if (strict) {
            resp.push(
                `${elem.tagName.toLowerCase()}[normalize-space(@id)="${quoteattr(elem.id)}"]`
            )
        } else {
            resp.push(
                `${elem.tagName.toLowerCase()}[@id="${quoteattr(elem.id)}"]`
            )
        }
    }
}

function getByMeta(elem: HTMLElement, resp: Array<string>, strict: boolean) {
    // if (elem.tagName.toLowerCase() != "meta") {
    //     return
    // }

    for (const attr_name of ["property", "name", "itemprop"]) {
        const val = elem.getAttribute(attr_name);
        if (val) {
            resp.push(`${elem.tagName.toLowerCase()}[@${attr_name}="${quoteattr(val)}"]`);
        }
    }
}

export function buildSelectorVariants(elem: HTMLElement, strict: boolean): Array<string> {
    const variants = new Array<string>();

    variants.push(elem.tagName.toLowerCase());
    getByMeta(elem, variants, strict);
    getByClass(elem, variants, strict);
    getByID(elem, variants, strict);

    return variants;
}

interface Candidate {
    elem: HTMLElement;
    xpath: string;
}

/*
Получает варианты написания xpath селектора для указанной ноды
 */
function getDirectPathCandidates(elem: HTMLElement, resp: Array<Candidate>, strict: boolean, relative: boolean) {
    const candidates = buildSelectorVariants(elem, strict);
    const prefix = relative ? ".//" : "//"
    for (const selector of candidates) {
        resp.push({
            elem: elem,
            xpath: prefix + selector,
        });
    }
}


/*
Обрабатываем случаи, когда есть четко один потомок, пример:

<div><a href="..." class="author-link">TEST</a></div>

Если юзер кликнул на div, то имеет смысл проверить идинственного ребенка, вдруг у него есть уникальный класс или ID.
Потомков проверяем рекурсивно.

В результате - прямой xpath до потомка.
*/
function getFirstChildsPathsCandidates(elem: HTMLElement, resp: Array<Candidate>, use_children: boolean = true, strict: boolean, root: HTMLElement) {
    if (!use_children) {
        return;
    }

    if (elem.childElementCount != 1) {
        return
    }

    // @ts-ignore
    for (const node of elem.childNodes) {
        if (node.nodeType == Node.TEXT_NODE) {
            return;
        }
    }

    const child = elem.children[0]! as HTMLElement;
    getDirectPathCandidates(child, resp, strict, false);
    getFirstChildsPathsCandidates(child, resp, true, strict, root);
    getFullPathCandidates(child, resp, strict, root);
}

function getFullPathCandidates(elem: HTMLElement, resp: Array<Candidate>, strict: boolean, root: HTMLElement) {
    const parent = elem.parentElement;
    if (!parent) {
        return
    }

    const xpath = getXPath(parent, false, strict, root);

    const candidates = buildSelectorVariants(elem, strict);
    for (const selector of candidates) {
        resp.push({
            elem: elem,
            xpath: `${xpath}/${selector}`,
        });
    }
}

interface XPathCandidateInfo {
    xpath: string;

    num_alternatives: number;
    candidate_idx: number;
    xpath_size: number;
}

function _get_elem_index(candidate_idx: number, num_alternatives: number): string {
    if (num_alternatives > 2 && candidate_idx > (num_alternatives + 1) / 2) {
        if ((candidate_idx - num_alternatives) == 0) {
            return 'last()';
        }

        return `last()${candidate_idx - num_alternatives}`
    }

    return candidate_idx.toString();
}


function getXpathSize(xpath: string): number {
    return xpath.replace("//", "").split("/").length
}

function getCandidateInfo(document: Document, candidate: Candidate, root: HTMLElement): XPathCandidateInfo {
    const resp = document.evaluate(candidate.xpath, root, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE);
    const alternatives = [];
    for (let i = 0; i < resp.snapshotLength; i++) {
        alternatives.push(resp.snapshotItem(i));
    }

    const num_alternatives = alternatives.length;
    const candidate_idx = alternatives.findIndex((x) => x == candidate.elem) + 1;

    let precise_path = candidate.xpath;
    if (alternatives.length > 1) {
        const parents = new Set(alternatives.map((x) => x!.parentElement!));
        const elem_idx = _get_elem_index(candidate_idx, num_alternatives);

        if (parents.size == 1) {
            precise_path = `${candidate.xpath}[${elem_idx}]`
        } else {
            precise_path = `(${candidate.xpath})[${elem_idx}]`
        }
    }

    return {
        xpath: precise_path,

        num_alternatives: num_alternatives,
        candidate_idx: candidate_idx,
        xpath_size: getXpathSize(precise_path),
    }
}


function getXPath(elem: HTMLElement, use_children: boolean = true, strict: boolean = true, root?: HTMLElement | null) {
    const document = elem.ownerDocument;
    if (!root) {
        root = document.getRootNode() as HTMLElement;
    }
    if (root == elem) {
        return ".";
    }

    const candidates = new Array<Candidate>();
    getDirectPathCandidates(elem, candidates, strict, root != document.getRootNode());
    getFirstChildsPathsCandidates(elem, candidates, use_children, strict, root);
    getFullPathCandidates(elem, candidates, strict, root);


    const allCandidates = [];
    let minimalXpath: XPathCandidateInfo | null = null;
    for (const candidate of candidates) {
        const cand = getCandidateInfo(document, candidate, root);
        allCandidates.push(cand.xpath);
        // console.log("check:", cand.xpath, cand)

        if (cand.num_alternatives == 1) {
            return cand.xpath;
        }

        if (minimalXpath == null) {
            minimalXpath = cand;
            continue
        }

        if (
            minimalXpath.xpath_size > cand.xpath_size ||
            minimalXpath.candidate_idx > cand.candidate_idx ||
            minimalXpath.num_alternatives > cand.num_alternatives
        ) {
            minimalXpath = cand;

        }
    }

    // if (use_children) {
    //     console.log("in", allCandidates);
    // }

    return minimalXpath!.xpath;
}
/*
портированный алгоритм из template_service.

`use_children` - контролирует рекурсию.
*/
export function getXPathForField(elem: HTMLElement, use_children: boolean, strict: boolean = true, root: HTMLElement | null, type: string | null): string {
    const xpath = getXPath(elem, use_children, strict, root);

    if(type=="url" && elem.tagName.toLowerCase() == "a") {
        return concatXpath(xpath, "@href")
    }

    return xpath;
}
